import { gql } from "@apollo/client";
import { Table, TableBody, TableCell, TableHead } from "@mui/material";
import makeStyles from "../../../../makeStylesV4Compat";
import clsx from "clsx";
import { AssembleTypography } from "../../../../components/AssembleTypography";
import { CompCycleDisplay } from "../../../../components/CompCycle/types";
import { CompComponentDisplay } from "../../../../models/Budget";
import { GRAY_6, GRAY_8, WHITE } from "../../../../theme";
import {
  Actual_compCycleBudget as CompCycleBudget,
  Actual_compCycleData as CompCycleData,
  Actual_manager as Manager,
  Actual_reports as Report,
} from "../../../../__generated__/graphql";
import { ReportingActualTier } from "./ReportingActualTier";

type Props = {
  manager: Manager | null;
  reports: Report[];
  rootBudget: CompCycleBudget | null;
  organizationName: string;
  compCycleData: CompCycleData;
  varianceDisplay: CompCycleDisplay;
};

const useStyles = makeStyles(() => ({
  root: {
    "& td, & th": {
      borderBottom: `1px solid ${GRAY_6}`,
      borderRight: `1px solid ${GRAY_6}`,
    },
    paddingBottom: "100px",
  },
  header: {
    backgroundColor: WHITE,
    height: "2rem",
    borderTop: `1px solid ${GRAY_6}`,
  },
  subheader: {
    backgroundColor: GRAY_8,
    height: "2rem",
  },
  groupedHeader: {
    "th&": {
      borderRight: `1px solid ${GRAY_6}`,
    },
  },
  name: {
    width: "20%",
  },
  directAndIndirect: {
    width: "5%",
  },
  totalActual: {
    width: "70%",
  },
  totalActualSubheader: {
    width: "23.33%",
  },
  scrollContainer: {
    overflowX: "auto",
    zIndex: 1,
    height: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  secondHeader: {
    top: "2rem",
    boxShadow: "0px 1px 5px rgba(10, 36, 64, 0.1)",
  },
}));

export function Actual({
  manager,
  reports,
  rootBudget,
  organizationName,
  compCycleData,
  varianceDisplay,
}: Props): JSX.Element {
  const classes = useStyles();

  return (
    <div className={classes.scrollContainer}>
      <Table className={classes.root}>
        {/* Main header */}
        <TableHead className={classes.stickyHeader}>
          <TableCell
            className={clsx(
              classes.header,
              classes.name,
              classes.groupedHeader
            )}
            variant="head"
          >
            <AssembleTypography variant="productTableHeader">
              Name
            </AssembleTypography>
          </TableCell>

          <TableCell
            className={clsx(
              classes.header,
              classes.groupedHeader,
              classes.directAndIndirect
            )}
            variant="head"
          >
            <AssembleTypography variant="productTableHeader">
              Direct
            </AssembleTypography>
          </TableCell>

          <TableCell
            className={clsx(classes.header, classes.directAndIndirect)}
            variant="head"
          >
            <AssembleTypography variant="productTableHeader">
              Indirect
            </AssembleTypography>
          </TableCell>

          {compCycleData.allowBonus && (
            <TableCell
              className={clsx(classes.header, classes.groupedHeader)}
              variant="head"
              colSpan={3}
              align="left"
            >
              <AssembleTypography variant="productTableHeader">
                {CompComponentDisplay.bonus}
              </AssembleTypography>
            </TableCell>
          )}
          {compCycleData.allowActualRecurringBonus && (
            <TableCell
              className={clsx(classes.header, classes.groupedHeader)}
              variant="head"
              colSpan={3}
              align="left"
            >
              <AssembleTypography variant="productTableHeader">
                {CompComponentDisplay.actualRecurringBonus}
              </AssembleTypography>
            </TableCell>
          )}
        </TableHead>

        {/* Subheader */}
        <TableHead className={clsx(classes.stickyHeader, classes.secondHeader)}>
          <TableCell
            className={clsx(
              classes.subheader,
              classes.groupedHeader,
              classes.name
            )}
            variant="head"
            align="left"
          />
          <TableCell
            className={clsx(
              classes.subheader,
              classes.groupedHeader,
              classes.directAndIndirect
            )}
            variant="head"
            align="left"
          />
          <TableCell
            className={clsx(
              classes.subheader,
              classes.groupedHeader,
              classes.directAndIndirect
            )}
            variant="head"
            align="left"
          />

          {/* One Time Bonus subheader cells */}
          {compCycleData.allowBonus && (
            <>
              <TableCell
                className={clsx(classes.subheader, classes.groupedHeader)}
                variant="head"
                align="left"
              >
                <AssembleTypography variant="productTableHeader">
                  Budget
                </AssembleTypography>
              </TableCell>
              <TableCell
                className={clsx(classes.subheader, classes.groupedHeader)}
                variant="head"
                align="left"
              >
                <AssembleTypography variant="productTableHeader">
                  Request
                </AssembleTypography>
              </TableCell>
              <TableCell
                className={clsx(classes.subheader)}
                variant="head"
                align="left"
              >
                <AssembleTypography variant="productTableHeader">
                  Usage
                </AssembleTypography>
              </TableCell>
            </>
          )}
          {/* Actual Recurring Bonus subheader cells */}
          {compCycleData.allowActualRecurringBonus && (
            <>
              <TableCell
                className={clsx(classes.subheader, classes.groupedHeader)}
                variant="head"
                align="left"
              >
                <AssembleTypography variant="productTableHeader">
                  Budget
                </AssembleTypography>
              </TableCell>
              <TableCell
                className={clsx(classes.subheader, classes.groupedHeader)}
                variant="head"
                align="left"
              >
                <AssembleTypography variant="productTableHeader">
                  Request
                </AssembleTypography>
              </TableCell>
              <TableCell
                className={clsx(classes.subheader)}
                variant="head"
                align="left"
              >
                <AssembleTypography variant="productTableHeader">
                  Usage
                </AssembleTypography>
              </TableCell>
            </>
          )}
        </TableHead>

        <TableBody>
          <ReportingActualTier
            manager={manager}
            reports={reports}
            rootBudget={rootBudget}
            organizationName={organizationName}
            varianceDisplay={varianceDisplay}
            compCycleData={compCycleData}
            indentation={[]}
          />
        </TableBody>
      </Table>
    </div>
  );
}

Actual.fragments = {
  compCycleBudget: gql`
    ${ReportingActualTier.fragments.compCycleBudget}
    fragment Actual_compCycleBudget on CompCycleBudget {
      compCycleId
      employeeId
      ...ReportingActualTier_compCycleBudget
    }
  `,
  manager: gql`
    ${ReportingActualTier.fragments.manager}
    fragment Actual_manager on Employee {
      id
      ...ReportingActualTier_manager
    }
  `,
  reports: gql`
    ${ReportingActualTier.fragments.reports}
    fragment Actual_reports on Employee {
      id
      ...ReportingActualTier_reports
    }
  `,
  compCycleData: gql`
    ${ReportingActualTier.fragments.compCycleData}
    fragment Actual_compCycleData on CompCycle {
      id
      allowBonus
      allowActualRecurringBonus
      ...ReportingActualTier_compCycleData
    }
  `,
};
